import React, { ReactElement } from 'react';
import { ListRenderer } from '@feast-it/ui/light';
import { PageProperties } from '../../../types';
import { toSentenceCase } from '../../../modules/formatting';
import { trackEvent } from '../../../modules/analytics';
import { LOCAL_LINK_CLICKED } from '../../../constants/analytics';
import { useRouter } from 'next/router';
import { getAbsoluteUrl } from '@/helpers/formatUrl';
import { SliceLayout } from '../../../components/SliceLayout';
import {
  Container,
  LocationLinksHeading,
  LocationLinksSubtitle,
  LocationLinksSubtitleLink,
  LocationLinksNavigationMultiple,
  LocationLinksNavigationSingle,
  LocationLinksSection,
  LocationLinksSectionSingle,
  LocationLinkItem,
  LocationLink,
  LocationText,
} from './LocationLinks.styled';

const getHeading = (seoVertical: string) => {
  return `Looking for ${
    seoVertical.toLowerCase() === 'venues' ? 'venues that are' : 'something'
  }  more local?`;
};

interface VerticalLocationLinksProps {
  pageProperties: PageProperties;
}

export function LocationLinksV2({
  pageProperties,
}: VerticalLocationLinksProps): ReactElement {
  const router = useRouter();
  const { pageContent, locationVariable, isCountry, isRegion } = pageProperties;
  const { seoVertical, locationLinks, nearMePageId, seoTagCopy } = pageContent;

  const isVenue = seoVertical === 'venues';
  const verticalTitle = isVenue ? 'venues' : 'suppliers';

  if (!locationLinks?.length) {
    return null;
  }

  const hasOneSection = locationLinks.length === 1;

  return (
    <SliceLayout backgroundColor="white">
      <Container>
        <LocationLinksHeading as="h2" style="headingMobileExtraLarge">
          {getHeading(seoVertical)}
        </LocationLinksHeading>

        {(!locationVariable || isCountry || isRegion) && nearMePageId && (
          <LocationLinksSubtitle style="bodyMedium" color="darkGrey">
            Take a look at our {verticalTitle} in the locations below or find{' '}
            <LocationLinksSubtitleLink href={nearMePageId}>
              {seoTagCopy ? seoTagCopy : seoVertical.toLowerCase()} near you
            </LocationLinksSubtitleLink>
            .
          </LocationLinksSubtitle>
        )}

        {hasOneSection ? (
          // Single section layout - apply columns to the section itself
          <LocationLinksNavigationSingle>
            <LocationLinksSectionSingle>
              <ListRenderer
                items={locationLinks[0]}
                render={({ item: link }) => (
                  <LocationLinkItem>
                    {link.path ? (
                      <LocationLink
                        href={link.path}
                        prefetch={false}
                        onClick={() => {
                          trackEvent(LOCAL_LINK_CLICKED, {
                            pageTitle: link.title,
                            destinationUrl: `${getAbsoluteUrl(link.path)}`,
                            currentPage: getAbsoluteUrl(router.asPath),
                            cuisine: link.seoTag ? link.seoTag : undefined,
                            town: link.town ?? undefined,
                            county: link.county ?? undefined,
                          });
                        }}
                      >
                        {toSentenceCase(link.title)}
                      </LocationLink>
                    ) : (
                      <LocationText>{toSentenceCase(link.title)}</LocationText>
                    )}
                  </LocationLinkItem>
                )}
              />
            </LocationLinksSectionSingle>
          </LocationLinksNavigationSingle>
        ) : (
          // Multiple sections layout - apply columns to the navigation container
          <LocationLinksNavigationMultiple>
            <ListRenderer
              items={locationLinks}
              render={({ item: section }) => (
                <LocationLinksSection>
                  <ListRenderer
                    items={section}
                    render={({ item: link }) => (
                      <LocationLinkItem>
                        {link.path ? (
                          <LocationLink
                            href={link.path}
                            prefetch={false}
                            onClick={() => {
                              trackEvent(LOCAL_LINK_CLICKED, {
                                pageTitle: link.title,
                                destinationUrl: `${getAbsoluteUrl(link.path)}`,
                                currentPage: getAbsoluteUrl(router.asPath),
                                cuisine: link.seoTag ? link.seoTag : undefined,
                                town: link.town ?? undefined,
                                county: link.county ?? undefined,
                              });
                            }}
                          >
                            {toSentenceCase(link.title)}
                          </LocationLink>
                        ) : (
                          <LocationText>
                            {toSentenceCase(link.title)}
                          </LocationText>
                        )}
                      </LocationLinkItem>
                    )}
                  />
                </LocationLinksSection>
              )}
            />
          </LocationLinksNavigationMultiple>
        )}
      </Container>
    </SliceLayout>
  );
}
